"use client"

import { RetrieveQuotePopup } from "@/shared/components/RetrieveQuotePopup/retrieve-quote-popup"
import { SkeletonPage } from "@dalmatech/ui"
import { Providers } from "@/shared/components/providers"
import { useMounted } from "@/shared/hooks/use-mounted"
import { formatQuoteResponse } from "@/shared/hooks/use-quote-fetching"
import { useRetrieveQuote } from "@/shared/hooks/use-retrieve-quote"
import { ROUTES } from "@/shared/routing/routes"
import { getQuote } from "@/shared/http"
import { QuoteLayout } from "@/shared/layouts/quote-layout"
import { computeRedirectRoute } from "@/shared/utils/compute-redirect-route"
import { deleteQuoteIdCookie, saveQuoteIdCookie } from "@/shared/utils/quote-cookie"
import {
  getQuoteIdFromStorage,
  removeQuoteIdFromStorage,
  storeQuoteId,
  storeUserId,
} from "@/shared/utils/storage"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { type Route } from "next"
import { usePathname, useRouter } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import React, { useEffect } from "react"
import { type PropsWithChildren } from "react"

const Layout = ({ children }: PropsWithChildren) => {
  const isMounted = useMounted()
  const pathname = usePathname()
  const router = useRouter()
  const [state, send] = useActor(newSubscriptionFlowService)
  const posthog = usePostHog()
  const { showPopup, isCheckingCookie, petNames, quoteId, quoteResponse, dismiss } =
    useRetrieveQuote()

  useEffect(() => {
    if (!isMounted || isCheckingCookie || showPopup) {
      return
    }
    const quoteIdFromStorage = getQuoteIdFromStorage()
    const isRecapOrPayment =
      pathname.includes(ROUTES.recap.path) || pathname.includes(ROUTES.payment.path)
    if (!state.context.quote?.meta?.quote_id && quoteIdFromStorage && isRecapOrPayment) {
      const fetchQuote = async () => {
        try {
          const quote = await getQuote(quoteIdFromStorage)
          const formattedQuote = formatQuoteResponse(
            quote,
            undefined,
            undefined,
            state.context.quote?.meta?.user_id
          )
          storeUserId(formattedQuote.meta?.user_id)
          send({
            type: "UPDATE_QUOTE",
            quote: formattedQuote,
          })
          send({
            type: "SET_RC_CONTRACT_ID",
            rc_contract_id: quote.data[0]?.rc_contract_id,
          })
        } catch {
          router.push(ROUTES.petName.path, { scroll: true })
        }
      }
      void fetchQuote()
    } else if (
      !Boolean(state.context.quote?.pets?.length) &&
      !isRecapOrPayment &&
      pathname !== ROUTES.petName.path
    ) {
      removeQuoteIdFromStorage()
      router.push(ROUTES.petName.path, { scroll: true })
    }
  }, [isMounted, pathname, state, isCheckingCookie, showPopup, router, send])

  const handleStartNew = () => {
    posthog.capture("retrieve_quote_decision", { has_retrieved: false, quote_id: quoteId })
    deleteQuoteIdCookie()
    dismiss()
  }

  const handleRetrieve = () => {
    if (!quoteResponse || !quoteId) return
    posthog.capture("retrieve_quote_decision", { has_retrieved: true, quote_id: quoteId })
    const formattedQuote = formatQuoteResponse(quoteResponse)
    storeQuoteId(quoteId)
    storeUserId(formattedQuote.meta?.user_id)
    saveQuoteIdCookie(quoteId)
    send({ type: "UPDATE_QUOTE", quote: formattedQuote })
    send({
      type: "SET_RC_CONTRACT_ID",
      rc_contract_id: quoteResponse.data[0]?.rc_contract_id,
    })

    const redirectRoute = computeRedirectRoute(quoteResponse)

    const petWithoutFormula = formattedQuote.pets?.find((p) => !p.pricing)
    const targetPet =
      redirectRoute === ROUTES.prepackages.path && petWithoutFormula
        ? petWithoutFormula
        : formattedQuote.pets?.[0]
    if (targetPet) {
      send({ type: "SET_CURRENT_PET_IDX", petId: targetPet.id, petIndex: targetPet.idx })
    }

    dismiss()
    router.push(redirectRoute as Route, { scroll: true })
  }

  const isReady = isMounted && !isCheckingCookie && !showPopup

  return (
    <Providers>
      <QuoteLayout>{isReady ? children : <SkeletonPage />}</QuoteLayout>
      <RetrieveQuotePopup
        open={showPopup}
        petNames={petNames}
        onStartNew={handleStartNew}
        onRetrieve={handleRetrieve}
      />
    </Providers>
  )
}

export default Layout
